import React, { useState, useEffect } from "react";
import { Document, Packer, Header, Paragraph, HeightRule, TextRun, convertInchesToTwip, PageNumber, SimpleField, NumberFormat, Table, TableRow, Footer, TableCell, AlignmentType, WidthType, BorderStyle, ImageRun, VerticalAlign, VerticalPositionAlign, VerticalPositionRelativeFrom, HorizontalPositionAlign, HorizontalPositionRelativeFrom, TextWrappingType, } from "docx";
import { saveAs } from "file-saver";
import "./Cables.css";
import { API_URL } from "./API";

const phases = ["R - E", "Y - E", "B - E"];
const hipotPhases = ["R-Phase", "Y-Phase", "B-Phase"];
const voltageOptions = ["250V", "500V", "1000V", "2500V", "5000V"];
const unitOptions = ["Ω", "KΩ", "MΩ", "TΩ", "GΩ"];
const runOptions = ["Run-1", "Run-2", "Run-3", "Run-4"];
const testOptions = ["Test-1", "Test-2", "Test-3", "Test-4"];

const CableTestReport = ({ clearAllFormData, project, goBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCableRuns, setSelectedCableRuns] = useState("Run-1");
  const [selectedHipotTests, setSelectedHipotTests] = useState("Test-1");
  const [originalFilename, setOriginalFilename] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    description: "",
    customer: "",
    client: ""
  });

  const [projectDetails, setProjectDetails] = useState({
    subProjectName: "",
    categoryName: "",
    subcategoryName: "",
    equipmentName: "",
    projectId: "",
    location: ""
  });


  const fetchClientName = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/clients/get_client`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
      });

      if (response.ok) {
        const result = await response.json();
        let clientsData = Array.isArray(result) ? result : result.data || result.clients || [];
        const client = clientsData.find(c => c.client_id === clientId || c.id === clientId);
        return client ? client.client_name : "Unknown Client";
      }
    } catch (error) {
     
    }
    return "Unknown Client";
  };

  const [form, setForm] = useState({
    description: "",
    date: "",
    customer: "",
    project: "",
    reportNo: "",
    location: "",
    client: "",
    cablesize: "",
    cablelength: "",
    cable_run_tests: "Run-1",
    hipot_run_tests: "Test-1"
  });


  useEffect(() => {
    const loadProjectData = async () => {
      if (project) {
        let clientName = project?.clientName || project?.client_name;

        if (!clientName && project?.client) {
          clientName = await fetchClientName(project.client);
        }

        setForm(prev => ({
          ...prev,
          project: project?.name || '',
          client: clientName || ''
        }));


        setProjectDetails(prev => ({
          ...prev,
          projectId: project?.id || project?.project_id || '',
          location: project?.location || ''
        }));

       
      }
    };

    loadProjectData();
  }, [project]);

  const getNewPhaseValues = () =>
    phases.map(() => ({ voltage: "", measuredValue: "", measuredUnit: "" }));

  const getNewHipotValues = () =>
    hipotPhases.map(() => ({ voltage: "", leakage: "" }));

  const getInitialTestData = (cableRuns = "Run-1", hipotTests = "Test-1") => {
    const cableRunNumber = parseInt(cableRuns.replace("Run-", ""));
    const hipotTestNumber = parseInt(hipotTests.replace("Test-", ""));

    const testData = {};

    for (let i = 1; i <= cableRunNumber; i++) {
      testData[`beforeRun${i}`] = getNewPhaseValues();
      testData[`afterRun${i}`] = getNewPhaseValues();
    }

    for (let i = 1; i <= hipotTestNumber; i++) {
      testData[`hipotRun${i}`] = getNewHipotValues();
    }

    return testData;
  };

  const [testData, setTestData] = useState(() => getInitialTestData("Run-1", "Test-1"));

  const getFilteredTestData = (data, cableRuns, hipotTests) => {
    const cableRunNumber = parseInt(cableRuns.replace("Run-", ""));
    const hipotTestNumber = parseInt(hipotTests.replace("Test-", ""));

    const filteredData = {};

    for (let i = 1; i <= cableRunNumber; i++) {
      if (data[`beforeRun${i}`]) {
        filteredData[`beforeRun${i}`] = data[`beforeRun${i}`];
      }
      if (data[`afterRun${i}`]) {
        filteredData[`afterRun${i}`] = data[`afterRun${i}`];
      }
    }

    for (let i = 1; i <= hipotTestNumber; i++) {
      if (data[`hipotRun${i}`]) {
        filteredData[`hipotRun${i}`] = data[`hipotRun${i}`];
      }
    }

    return filteredData;
  };

  const saveFilteredTestDataToStorage = (data, cableRuns, hipotTests) => {
    const filteredData = getFilteredTestData(data, cableRuns, hipotTests);
    localStorage.setItem('cableTestData', JSON.stringify(filteredData));
    return filteredData;
  };

  const clearAllStorageData = () => {
    localStorage.removeItem('cableTestFormData');
    localStorage.removeItem('cableTestData');

    setForm({
      description: "",
      date: "",
      customer: "",
      project: "",
      reportNo: "",
      location: "",
      client: "",
      cablesize: "",
      cablelength: "",
      cable_run_tests: "Run-1",
      hipot_run_tests: "Test-1"
    });

    setTestData(getInitialTestData("Run-1", "Test-1"));
    setSelectedCableRuns("Run-1");
    setSelectedHipotTests("Test-1");
    setValidationErrors({
      description: "",
      customer: "",
      client: ""
    });
    setProjectDetails({
      subProjectName: "",
      categoryName: "",
      subcategoryName: "",
      equipmentName: "",
      projectId: "",
      location: ""
    });
    setFormSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const loadAllFormDataFromStorage = () => {
    try {
      const allFormData = {
        form: {},
        testData: {}
      };

      const cableFormData = localStorage.getItem('cableFormData');
      if (cableFormData) {
        const parsedCableFormData = JSON.parse(cableFormData);
        allFormData.form = parsedCableFormData;
      }

      const cableTestData = localStorage.getItem('cableTestData');
      if (cableTestData) {
        const parsedTestData = JSON.parse(cableTestData);
        allFormData.testData = parsedTestData;
      }

      return allFormData;
    } catch (error) {

      return {
        form: {},
        testData: {}
      };
    }
  };

  const resetAllFormStates = () => {
    setProjectDetails({
      subProjectName: "",
      categoryName: "",
      subcategoryName: "",
      equipmentName: "",
      projectId: "",
      location: ""
    });

    setFormSubmitted(false);
    setErrorMessage("");
    setSuccessMessage("");

    if (clearAllFormData && typeof clearAllFormData === 'function') {
      clearAllFormData();
    }
  };

  
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        const updateReportData = localStorage.getItem('updateReportData');
        if (updateReportData) {
          const parsedData = JSON.parse(updateReportData);
        
          
          if (parsedData.report_file && parsedData.report_file.filename) {
            setOriginalFilename(parsedData.report_file.filename);
          }

          if (parsedData.test_data && parsedData.test_data.form) {
            const existingForm = parsedData.test_data.form;
            setForm({
              description: existingForm.description || "CABLES INSPECTION / TEST REPORT",
              date: existingForm.date || parsedData.test_date || new Date().toISOString().split('T')[0],
              customer: existingForm.customer || '',
              project: existingForm.project || parsedData.sub_project_name || '',
              reportNo: existingForm.reportNo || parsedData.report_no || '',
              location: existingForm.location || parsedData.location || '',
              client: existingForm.client || parsedData.client_name || '',
              cablesize: existingForm.cablesize || '',  
              cablelength: existingForm.cablelength || '', 
              cable_run_tests: existingForm.cable_run_tests || "Run-1",
              hipot_run_tests: existingForm.hipot_run_tests || "Test-1"
            });

            setSelectedCableRuns(existingForm.cable_run_tests || "Run-1");
            setSelectedHipotTests(existingForm.hipot_run_tests || "Test-1");
          }

          if (parsedData.test_data && parsedData.test_data.testData) {
            const convertedTestData = {};
            const existingTestData = parsedData.test_data.testData;

            Object.keys(existingTestData).forEach(testKey => {
              if (testKey.startsWith('beforeRun') || testKey.startsWith('afterRun')) {
                const testValues = existingTestData[testKey];
                if (typeof testValues === 'object' && !Array.isArray(testValues)) {
                  const phaseArray = [];
                  phases.forEach((phase, index) => {
                    phaseArray.push({
                      voltage: testValues.voltage?.[`reading_${index + 1}`] || '',
                      measuredValue: testValues.measuredValue?.[`reading_${index + 1}`] || '',
                      measuredUnit: testValues.measuredUnit?.[`reading_${index + 1}`] || ''
                    });
                  });
                  convertedTestData[testKey] = phaseArray;
                } else if (Array.isArray(testValues)) {
                  convertedTestData[testKey] = testValues;
                }
              } else if (testKey.startsWith('hipotRun')) {
                const hipotValues = existingTestData[testKey];
                if (typeof hipotValues === 'object' && !Array.isArray(hipotValues)) {
                  const hipotArray = [];
                  hipotPhases.forEach((phase, index) => {
                    hipotArray.push({
                      voltage: hipotValues.voltage?.[`reading_${index + 1}`] || '',
                      leakage: hipotValues.leakage?.[`reading_${index + 1}`] || ''
                    });
                  });
                  convertedTestData[testKey] = hipotArray;
                } else if (Array.isArray(hipotValues)) {
                  convertedTestData[testKey] = hipotValues;
                }
              }
            });

            setTestData(convertedTestData);
            localStorage.setItem('cableTestData', JSON.stringify(convertedTestData));
          }

          setProjectDetails({
            subProjectName: parsedData.sub_project_name || "",
            categoryName: parsedData.category_name || "",
            subcategoryName: parsedData.subcategory_name || "",
            equipmentName: parsedData.equipment_name || "",
            projectId: parsedData.project_id || "",
            location: parsedData.location || ""
          });
        }
      } catch (error) {
      
      }
    };

    loadSavedData();
  }, []); 

  
  useEffect(() => {
    const syncProjectData = async () => {
      if (project) {
        let clientName = project?.clientName || project?.client_name;

        if (!clientName && project?.client) {
          clientName = await fetchClientName(project.client);
        }

      
        setProjectDetails(prev => ({
          ...prev,
          projectId: project?.project_id || project?.id || prev.projectId,
          location: project?.location || prev.location
        }));

        
      }
    };
    syncProjectData();
  }, [project]);


  const validateDescriptionInput = (value) => {
    const descriptionRegex = /^[A-Za-z\s/ ]*$/;
    return descriptionRegex.test(value);
  };

  const validateReportLocationInput = (value) => {
    const reportLocationRegex = /^[A-Za-z0-9\-,s ]*$/;
    return reportLocationRegex.test(value);
  };

  const validateClientCustomerInput = (value) => {
    const clientCustomerRegex = /^[A-Za-z\s ]*$/;
    return clientCustomerRegex.test(value);
  };

  const validateProjectInput = (value) => {
    const projectRegex = /^[A-Za-z0-9\-,s ]*$/;
    return projectRegex.test(value);
  };

  const validateMeasuredValueInput = (value) => {
    const measuredValueRegex = /^[0-9.<> ]*$/;
    return measuredValueRegex.test(value) && value.length <= 10;
  };

  const validateAppliedVoltageInput = (value) => {
    const voltageRegex = /^[A-Za-z0-9 ]*$/;
    return voltageRegex.test(value);
  };

  const validateLeakageCurrentInput = (value) => {
    const numericRegex = /^[0-9. ]*$/;
    return numericRegex.test(value);
  };

  const handleFormChange = (key, value) => {

    if (key === 'description') {
      if (!validateDescriptionInput(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [key]: "Only alphabetic characters and '/' are allowed"
        }));
        return;
      }
    } else if (['reportNo', 'location'].includes(key)) {
      if (!validateReportLocationInput(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [key]: "Only alphanumeric characters and '-' are allowed"
        }));
        return;
      }
    } else if (['client', 'customer'].includes(key)) {
      if (!validateClientCustomerInput(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [key]: "Only alphabetic characters are allowed"
        }));
        return;
      }
    } else if (key === 'project') {
      if (!validateProjectInput(value)) {
        setValidationErrors(prev => ({
          ...prev,
          [key]: "Only alphanumeric characters are allowed"
        }));
        return;
      }
    }

    setValidationErrors(prev => ({
      ...prev,
      [key]: ""
    }));

    setForm(prev => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('cableTestFormData', JSON.stringify(updated));


      if (key === 'cable_run_tests') {
        setSelectedCableRuns(value);


        setTestData(prevTestData => {
          const newCableRunNumber = parseInt(value.replace("Run-", ""));
          const currentHipotTests = updated.hipot_run_tests || selectedHipotTests;

          const updatedTestData = { ...prevTestData };


          for (let i = 1; i <= newCableRunNumber; i++) {
            if (!updatedTestData[`beforeRun${i}`]) {
              updatedTestData[`beforeRun${i}`] = getNewPhaseValues();
            }
            if (!updatedTestData[`afterRun${i}`]) {
              updatedTestData[`afterRun${i}`] = getNewPhaseValues();
            }
          }


          Object.keys(updatedTestData).forEach(key => {
            if (key.startsWith('beforeRun') || key.startsWith('afterRun')) {
              const runNumber = parseInt(key.replace('beforeRun', '').replace('afterRun', ''));
              if (runNumber > newCableRunNumber) {
                delete updatedTestData[key];
              }
            }
          });


          saveFilteredTestDataToStorage(updatedTestData, value, currentHipotTests);
          return updatedTestData;
        });
      }

      if (key === 'hipot_run_tests') {
        setSelectedHipotTests(value);


        setTestData(prevTestData => {
          const newHipotTestNumber = parseInt(value.replace("Test-", ""));
          const currentCableRuns = updated.cable_run_tests || selectedCableRuns;


          const updatedTestData = { ...prevTestData };


          for (let i = 1; i <= newHipotTestNumber; i++) {
            if (!updatedTestData[`hipotRun${i}`]) {
              updatedTestData[`hipotRun${i}`] = getNewHipotValues();
            }
          }


          Object.keys(updatedTestData).forEach(key => {
            if (key.startsWith('hipotRun')) {
              const runNumber = parseInt(key.replace('hipotRun', ''));
              if (runNumber > newHipotTestNumber) {
                delete updatedTestData[key];
              }
            }
          });


          saveFilteredTestDataToStorage(updatedTestData, currentCableRuns, value);
          return updatedTestData;
        });
      }

      return updated;
    });
  };

  const handleTestChange = (section, index, field, value) => {

    if (field === 'measuredValue') {
      if (!validateMeasuredValueInput(value)) {
        return;
      }
    }

    if (field === 'voltage' && section.includes('hipot')) {
      if (!validateAppliedVoltageInput(value)) {
        return;
      }
    }

    if (field === 'leakage') {
      if (!validateLeakageCurrentInput(value)) {
        return;
      }
    }

    setTestData(prev => {
      const updated = { ...prev };

      if (!updated[section]) {
        updated[section] = section.includes('hipot') ? getNewHipotValues() : getNewPhaseValues();
      }

      if (!Array.isArray(updated[section]) || updated[section].length === 0) {
        updated[section] = section.includes('hipot') ? getNewHipotValues() : getNewPhaseValues();
      }

      updated[section] = [...updated[section]];

      if (!updated[section][index]) {
        updated[section][index] = section.includes('hipot')
          ? { voltage: "", leakage: "" }
          : { voltage: "", measuredValue: "", measuredUnit: "" };
      }

      updated[section][index] = { ...updated[section][index], [field]: value };


      saveFilteredTestDataToStorage(updated, selectedCableRuns, selectedHipotTests);

      return updated;
    });
  };

  const redirectToHome = () => {
    if (window.confirm("Are you sure you want to leave this page")) {
      localStorage.setItem('openReportsOnLoad', 'true');
      window.location.href = "/";
    }
  };

  const handleCancel = () => {
    window.location.reload();
  };

  const validateTextFields = () => {
    const validationMap = {
      'description': validateDescriptionInput,
      'customer': validateClientCustomerInput,
      'client': validateClientCustomerInput,
      'reportNo': validateReportLocationInput,
      'location': validateReportLocationInput,
      'project': validateProjectInput
    };

    let isValid = true;
    const newValidationErrors = { ...validationErrors };

    Object.entries(validationMap).forEach(([field, validationFn]) => {
      if (form[field]) {
        if (!validationFn(form[field])) {
          if (field === 'description') {
            newValidationErrors[field] = "Only alphabetic characters and '/' are allowed";
          } else if (['reportNo', 'location'].includes(field)) {
            newValidationErrors[field] = "Only alphanumeric characters and '-' are allowed";
          } else if (['client', 'customer'].includes(field)) {
            newValidationErrors[field] = "Only alphabetic characters are allowed";
          } else if (field === 'project') {
            newValidationErrors[field] = "Only alphanumeric characters are allowed";
          }
          isValid = false;
        } else {
          newValidationErrors[field] = "";
        }
      }
    });

    setValidationErrors(newValidationErrors);
    return isValid;
  };

  const validateForm = () => {
    if (!form.date || !form.project || !form.reportNo) {

      return false;
    }

    return validateTextFields();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setFormSubmitted(true);

    try {
      const allFormData = loadAllFormDataFromStorage ? loadAllFormDataFromStorage() : {};

      const safeFormData = form && typeof form === 'object' ? {
        form: form || allFormData.form,
        testData: testData || allFormData.testData
      } : { form: allFormData.form || {}, testData: allFormData.testData || {} };

     

      if (!validateForm()) {
        setIsLoading(false);
        return;
      }


      const updateReportData = localStorage.getItem('updateReportData');
      let reportId = null;

      if (updateReportData) {
        const parsedData = JSON.parse(updateReportData);
        reportId = parsedData.report_id || parsedData.id;
      
      }

      if (!reportId) {
        throw new Error("Report ID not found. Cannot update report.");
      }

      const convertArrayToDict = (testData) => {
        const convertedData = {};

        for (const [testType, testArray] of Object.entries(testData)) {
          if (Array.isArray(testArray)) {
            convertedData[testType] = {};
            testArray.forEach((item, index) => {
              if (typeof item === 'object' && item !== null) {
                for (const [key, value] of Object.entries(item)) {
                  if (!convertedData[testType][key]) {
                    convertedData[testType][key] = {};
                  }
                  convertedData[testType][key][`reading_${index + 1}`] = value;
                }
              } else {
                convertedData[testType][`reading_${index + 1}`] = item;
              }
            });
          } else {
            convertedData[testType] = testData[testType];
          }
        }

        return convertedData;
      };

      const convertedTestData = convertArrayToDict(safeFormData.testData || {});

      

      const wordBlob = await generateWordBlob(safeFormData.form, convertedTestData);

      const reader = new FileReader();
      reader.readAsDataURL(wordBlob);

      reader.onloadend = async () => {
        try {
          const base64data = reader.result;

          const currentDate = new Date().toISOString().split('T')[0];
          const formLocation = safeFormData.form.location || "";

          const getProjectId = () => {
           

            if (projectDetails.projectId) {
              const id = parseInt(projectDetails.projectId);
              if (!isNaN(id) && id > 0) {
              
                return id;
              }
            }
          

            if (project?.project_id) {
              const id = parseInt(project.project_id);
              if (!isNaN(id) && id > 0) {
                
                return id;
              }
            }
            if (project?.id) {
              const id = parseInt(project.id);
              if (!isNaN(id) && id > 0) {
              
                return id;
              }
            }
          

            try {
              const createReportData = localStorage.getItem('createReportData');
              if (createReportData) {
                const parsedData = JSON.parse(createReportData);
               

                if (parsedData.project_id) {
                  const id = parseInt(parsedData.project_id);
                  if (!isNaN(id) && id > 0) {
                  
                    return id;
                  }
                }
              }
            } catch (error) {
             
            }
        

            if (safeFormData.form.project_id) {
              const id = parseInt(safeFormData.form.project_id);
              if (!isNaN(id) && id > 0) {
                
                return id;
              }
            }
            
            alert("Warning: Could not determine project ID. Report may be saved incorrectly.");
            return 1;
          };

          const projectId = getProjectId();
         


          const apiRequestData = {
            report_id: parseInt(reportId),
            equipment_name: String(projectDetails.equipmentName || safeFormData.form.equipment || "Cable Equipment"),
            test_date: String(safeFormData.form.date || currentDate),
            category_name: String(projectDetails.categoryName || "Cables"),
            subcategory_name: String(projectDetails.subcategoryName || "Cable"),
            sub_project_name: String(projectDetails.subProjectName || safeFormData.form.project || "Project"),
            project_id: projectId,
            location: String(formLocation || projectDetails.location || ""),
            report_file: {
              report_word: base64data,
              filename: originalFilename || `Cable_Test_Report_${Date.now()}.docx`,
              file_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            },
            test_data: {
              form: safeFormData.form || {},
              testData: convertedTestData
            }
          };


          const response = await fetch(`${API_URL}/test_report/update_test_report`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(apiRequestData),
            mode: 'cors',
            credentials: 'same-origin',
          });

          if (!response.ok) {
            let errorData;
            try {
              errorData = await response.json();
             
            } catch (jsonError) {
             
              errorData = { message: response.statusText };
            }

            throw new Error(`API error: ${response.status} - ${errorData.message || errorData.detail || response.statusText}`);
          }

          const result = await response.json();
        

          alert("Data updated successfully");


          localStorage.removeItem('updateReportData');
          localStorage.removeItem('cableTestFormData');
          localStorage.removeItem('cableTestData');

        } catch (error) {
        
          setErrorMessage(`Error: ${error.message}`);
          alert(`Error saving data: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      };

      reader.onerror = () => {
        setIsLoading(false);
        setErrorMessage("Error reading Word file");
        alert("Error processing Word file");
      };

    } catch (error) {
      

      if (error.message.includes("Failed to fetch")) {
        setErrorMessage("Cannot connect to server. Please check if the backend is running.");
        alert("Cannot connect to server. Please check if the backend is running.");
      } else {
        setErrorMessage(`Error: ${error.message}`);
        alert(`Error saving data: ${error.message}`);
      }
      setIsLoading(false);
    }
  };



  const generateWordBlob = (formData, reportTestData) => {
    return new Promise((resolve, reject) => {
      try {
   
        const storedFormData = formData || {};
        const cableTestData = reportTestData || {};


        const tableBorders = {
          top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
        };


        const pageBorders = {
          top: { style: BorderStyle.SINGLE, size: 12, color: "000000", space: 24 },
          bottom: { style: BorderStyle.SINGLE, size: 12, color: "000000", space: 24 },
          left: { style: BorderStyle.SINGLE, size: 12, color: "000000", space: 24 },
          right: { style: BorderStyle.SINGLE, size: 12, color: "000000", space: 24 }
        };


        function findValue(data, searchTerms, defaultValue = "-") {
          if (!data || typeof data !== 'object') return defaultValue;

          const terms = Array.isArray(searchTerms) ? searchTerms : [searchTerms];

          for (let term of terms) {
            if (data[term] !== undefined && data[term] !== null && data[term] !== '') {
              return data[term];
            }
          }

          const dataKeys = Object.keys(data);
          for (let term of terms) {
            const termLower = term.toLowerCase();
            for (let key of dataKeys) {
              if (key.toLowerCase() === termLower && data[key] !== undefined && data[key] !== null && data[key] !== '') {
                return data[key];
              }
            }
          }

          for (let term of terms) {
            const termLower = term.toLowerCase();
            for (let key of dataKeys) {
              if (key.toLowerCase().includes(termLower) && data[key] !== undefined && data[key] !== null && data[key] !== '') {
                return data[key];
              }
            }
          }

          return defaultValue;
        }

        function safeToString(value) {
          if (value === null || value === undefined || value === '' || value === '-') {
            return "";
          }
          return value.toString();
        }


        const fetchImageAsBase64 = (url) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
              try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL('image/png');
                const base64 = dataURL.split(',')[1];
                resolve(base64);
              } catch (error) {
            
                resolve(null);
              }
            };
            img.onerror = function () {
             
              resolve(null);
            };
            img.src = url;
          });
        };


        const createLightWatermark = (base64Data) => {
          return new Promise((resolve) => {
            if (!base64Data) {
              resolve(null);
              return;
            }

            try {
              const img = new Image();
              img.onload = function () {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;


                ctx.globalAlpha = 0.15;
                ctx.drawImage(img, 0, 0);

                const lightDataURL = canvas.toDataURL('image/png');
                const lightBase64 = lightDataURL.split(',')[1];
                resolve(lightBase64);
              };
              img.onerror = function () {
                resolve(null);
              };
              img.src = 'data:image/png;base64,' + base64Data;
            } catch (error) {
             
              resolve(null);
            }
          });
        };


        const documentSections = [];


        const logoUrl = "https://i.ibb.co/BHKCMXV8/Whats-App-Image-2025-06-16-at-11-21-26-AM.jpg";

        const watermarkUrl = "https://i.ibb.co/BHKCMXV8/Whats-App-Image-2025-06-16-at-11-21-26-AM.jpg";

        const convertInchesToTwip = (inches) => {
          return Math.round(inches * 1440);
        };


        Promise.all([
          fetchImageAsBase64(logoUrl),
          fetchImageAsBase64(watermarkUrl)
        ]).then(([logoBase64, watermarkBase64]) => {


          createLightWatermark(watermarkBase64).then((lightWatermarkBase64) => {

            const headerElements = [];

            if (logoBase64) {
              headerElements.push(
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new ImageRun({
                                  data: logoBase64,
                                  transformation: {
                                    width: 80,
                                    height: 80
                                  }
                                })
                              ],
                              alignment: AlignmentType.CENTER,
                            })
                          ],
                          verticalAlign: VerticalAlign.CENTER,
                          borders: {
                            top: { style: BorderStyle.SINGLE, size: 1 },
                            bottom: { style: BorderStyle.SINGLE, size: 1 },
                            left: { style: BorderStyle.SINGLE, size: 1 },
                            right: { style: BorderStyle.SINGLE, size: 1 }
                          },
                          width: { size: 20, type: WidthType.PERCENTAGE },
                          margins: {
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0
                          }
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "PIE POWER PRIVATE LIMITED",
                                  font: "Times New Roman",
                                  color: "#1e402c",
                                  bold: true,
                                  size: 36
                                })
                              ],
                              alignment: AlignmentType.CENTER
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "VISAKHAPATNAM, ANDHRA PRADESH INDIA-531163",
                                  font: "helvetica",
                                  size: 20
                                })
                              ],
                              alignment: AlignmentType.CENTER
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: "Email: info@piepower.in, Web: piepower.in, Contact: +91 8186084709",
                                  font: "helvetica",
                                  size: 20
                                })
                              ],
                              alignment: AlignmentType.CENTER
                            })
                          ],
                          borders: {
                            top: { style: BorderStyle.SINGLE, size: 1 },
                            bottom: { style: BorderStyle.SINGLE, size: 1 },
                            left: { style: BorderStyle.SINGLE, size: 1 },
                            right: { style: BorderStyle.SINGLE, size: 1 }
                          },
                          width: { size: 80, type: WidthType.PERCENTAGE }
                        })
                      ]
                    })
                  ]
                })
              );
            } else {

              headerElements.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "PIE POWER PRIVATE LIMITED",
                      font: "helvetica",
                      bold: true,
                      size: 36
                    })
                  ],
                  alignment: AlignmentType.CENTER
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "VISAKHAPATNAM, ANDHRA PRADESH INDIA-531163",
                      font: "helvetica",
                      size: 20
                    })
                  ],
                  alignment: AlignmentType.CENTER
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Email: info@piepower.in, Web: piepower.in, Contact: +91 8186084709",
                      font: "helvetica",
                      size: 20
                    })
                  ],
                  alignment: AlignmentType.CENTER
                })
              );
            }

            headerElements.push(new Paragraph({ text: "" }));
            documentSections.push(...headerElements);




            documentSections.push(
              new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Description", font: "Courier New", bold: true })] })],
                        borders: tableBorders,
                        width: { size: 20, type: WidthType.PERCENTAGE }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "CABLES INSPECTION / TEST REPORT ", font: "Courier New", bold: true })] })],
                        borders: tableBorders,
                        width: { size: 50, type: WidthType.PERCENTAGE }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Date", font: "Courier New", bold: true })] })],
                        borders: tableBorders,
                        width: { size: 15, type: WidthType.PERCENTAGE }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(storedFormData?.date), font: "Courier New" })] })],
                        borders: tableBorders,
                        width: { size: 15, type: WidthType.PERCENTAGE }
                      })
                    ]
                  }),
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Project", font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(storedFormData?.project), font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Report No", font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(storedFormData?.reportNo), font: "Courier New" })] })],
                        borders: tableBorders
                      })
                    ]
                  }),
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Customer", font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(storedFormData?.customer), font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "", font: "Courier New", bold: true })] })],
                        borders: tableBorders

                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "", font: "Courier New" })] })],
                        borders: tableBorders
                      })
                    ]
                  }),
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Client", font: "Courier New", bold: true })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(storedFormData?.client), font: "Courier New", bold: true })] })],
                        borders: tableBorders,

                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "", font: "Courier New" })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "", font: "Courier New" })] })],
                        borders: tableBorders
                      })

                    ]
                  })
                ]
              })
            );

            documentSections.push(new Paragraph({ text: "" }));




            documentSections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "LOCATION:",
                    font: "Courier New",
                    bold: true,
                    size: 24
                  }),
                  new TextRun({
                    text: " " + safeToString(storedFormData?.location),
                    font: "Courier New",

                    size: 24
                  })
                ]
              })
            );

            documentSections.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "CABLE SIZE:",
                            font: "Courier New",
                             bold: true,
                            size: 24
                          }),
                           new TextRun({
                            text: " " + safeToString(storedFormData?.cablesize),
                            font: "Courier New",
                          
                            size: 24
                          })
                        ]
                      })
                    );
            
                   
                    
                  documentSections.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "CABLE LENGTH:",
                            font: "Courier New",
                             bold: true,
                            size: 24
                          }),
                           new TextRun({
                            text: " " + safeToString(storedFormData?.cablelength),
                            font: "Courier New",
                          
                            size: 24
                          })
                        ]
                      })
                    );
            
                    documentSections.push(new Paragraph({ text: "" })); 




            documentSections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Insulation Resistance Test Results",
                    font: "Courier New",
                    bold: true,
                    size: 24
                  })
                ]
              })
            );




            const formatResistanceValue = (measuredValue, measuredUnit) => {
              if (!measuredValue) return "-";
              const unit = measuredUnit || "";
              const ohm = "Ω";
              return `${measuredValue} ${unit.replace(/KΩ/g, `K${ohm}`).replace(/MΩ/g, `M${ohm}`).replace(/TΩ/g, `T${ohm}`).replace(/GΩ/g, `G${ohm}`).replace(/Ω/g, ohm)}`;
            };


            const phases = ["R - E", "Y - E", "B - E"];
            const selectedCableRuns = storedFormData?.cable_run_tests || "Run-1";
            const numCableRuns = parseInt(selectedCableRuns.replace("Run-", ""));

            for (let runNumber = 1; runNumber <= numCableRuns; runNumber++) {
              const beforeData = cableTestData[`beforeRun${runNumber}`] || [];
              const afterData = cableTestData[`afterRun${runNumber}`] || [];


              documentSections.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `BEFORE HIPOT INSULATION RESISTANCE TEST: RUN-${runNumber}`,
                      font: "Courier New",
                      bold: true,
                      size: 20
                    })
                  ],
                  spacing: { before: 200, after: 100 }
                })
              );

              const beforeRows = [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Phase", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Applied Voltage", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Measured Value", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 34, type: WidthType.PERCENTAGE }
                    })
                  ]
                })
              ];

              phases.forEach((phase, index) => {
                const phaseData = beforeData[index] || {};
                beforeRows.push(
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: phase, bold: true, font: "Courier New" })] })],
                        borders: tableBorders,
                        shading: { fill: "a6a6a6" }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(phaseData.voltage), font: "Courier New" })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: formatResistanceValue(phaseData.measuredValue, phaseData.measuredUnit), font: "Courier New" })] })],
                        borders: tableBorders
                      })
                    ]
                  })
                );
              });

              documentSections.push(
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: beforeRows
                })
              );

              documentSections.push(new Paragraph({ text: "" }));


              documentSections.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `AFTER HIPOT INSULATION RESISTANCE TEST: RUN-${runNumber}`,
                      font: "Courier New",
                      bold: true,
                      size: 20
                    })
                  ],
                  spacing: { before: 200, after: 100 }
                })
              );

              const afterRows = [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Phase", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Applied Voltage", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Measured Value", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 34, type: WidthType.PERCENTAGE }
                    })
                  ]
                })
              ];

              phases.forEach((phase, index) => {
                const phaseData = afterData[index] || {};
                afterRows.push(
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: phase, bold: true, font: "Courier New" })] })],
                        borders: tableBorders,
                        shading: { fill: "a6a6a6" }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(phaseData.voltage), font: "Courier New" })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: formatResistanceValue(phaseData.measuredValue, phaseData.measuredUnit), font: "Courier New" })] })],
                        borders: tableBorders
                      })
                    ]
                  })
                );
              });

              documentSections.push(
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: afterRows
                })
              );

              documentSections.push(new Paragraph({ text: "" }));
            }


            documentSections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "HIPOT Test Results",
                    font: "Courier New",
                    bold: true,
                    size: 24
                  })
                ],
                spacing: { before: 400, after: 200 }
              })
            );

            const hipotPhases = ["R-Phase", "Y-Phase", "B-Phase"];
            const selectedHipotTests = storedFormData?.hipot_run_tests || "Test-1";
            const numHipotTests = parseInt(selectedHipotTests.replace("Test-", ""));

            for (let testNumber = 1; testNumber <= numHipotTests; testNumber++) {
              const hipotData = cableTestData[`hipotRun${testNumber}`] || [];

              documentSections.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `HIPOT TEST: RUN-${testNumber}`,
                      font: "Courier New",
                      bold: true,
                      size: 20
                    })
                  ],
                  spacing: { before: 200, after: 100 }
                })
              );

              const hipotRows = [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Phase", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " DC Applied Voltage (KV)", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 33, type: WidthType.PERCENTAGE }
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: " Measured Leakage Current (mA)", font: "Courier New", bold: true })] })],
                      borders: tableBorders,
                      shading: { fill: "a6a6a6" },
                      width: { size: 34, type: WidthType.PERCENTAGE }
                    })
                  ]
                })
              ];

              hipotPhases.forEach((phase, index) => {
                const phaseData = hipotData[index] || {};
                hipotRows.push(
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: phase, bold: true, font: "Courier New" })] })],
                        borders: tableBorders,
                        shading: { fill: "a6a6a6" }
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(phaseData.voltage), font: "Courier New" })] })],
                        borders: tableBorders
                      }),
                      new TableCell({
                        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: safeToString(phaseData.leakage), font: "Courier New" })] })],
                        borders: tableBorders
                      })
                    ]
                  })
                );
              });

              documentSections.push(
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  rows: hipotRows
                })
              );

              documentSections.push(new Paragraph({ text: "" }));
            }


            const footerTable = new Table({
              width: { size: 100, type: WidthType.PERCENTAGE, },
              alignment: AlignmentType.LEFT,
              rows: [
                new TableRow({
                  height: { value: convertInchesToTwip(0.5), rule: HeightRule.ATLEAST },
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.LEFT,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: "Pie Power",
                              font: "Courier New",
                              bold: true,
                              size: 18
                            })
                          ]
                        }),
                      ],
                      borders: tableBorders,
                      width: { size: 25, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.TOP,
                      margins: {
                        top: convertInchesToTwip(0.1),
                        bottom: convertInchesToTwip(0.25),
                        left: convertInchesToTwip(0.25),
                        right: convertInchesToTwip(0.25)
                      }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.LEFT,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: "TPREL",
                              font: "Courier New",
                              bold: true,
                              size: 18
                            })
                          ]
                        }),
                      ],
                      borders: tableBorders,
                      width: { size: 25, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.TOP,
                      margins: {
                        top: convertInchesToTwip(0.1),
                        bottom: convertInchesToTwip(0.25),
                        left: convertInchesToTwip(0.25),
                        right: convertInchesToTwip(0.25)
                      }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.LEFT,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: "TPREL",
                              font: "Courier New",
                              bold: true,
                              size: 18
                            })
                          ]
                        }),
                      ],
                      borders: tableBorders,
                      width: { size: 25, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.TOP,
                      margins: {
                        top: convertInchesToTwip(0.1),
                        bottom: convertInchesToTwip(0.25),
                        left: convertInchesToTwip(0.25),
                        right: convertInchesToTwip(0.25)
                      }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.LEFT,
                          spacing: { before: 0, after: 0 },
                          children: [
                            new TextRun({
                              text: "TPREL",
                              font: "Courier New",
                              bold: true,
                              size: 18
                            }),
                            new SimpleField({
                              instruction: "PAGE",
                              children: [
                                new TextRun({
                                  text: "1",
                                  font: "Courier New",
                                  size: 16
                                })
                              ]
                            }),
                            new SimpleField({
                              instruction: "NUMPAGES",
                              children: [
                                new TextRun({
                                  text: "1",
                                  font: "Courier New",
                                  size: 16
                                })
                              ]
                            })
                          ]
                        }),
                      ],
                      borders: tableBorders,
                      width: { size: 25, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.TOP,
                      margins: {
                        top: convertInchesToTwip(0.1),
                        bottom: convertInchesToTwip(0.25),
                        left: convertInchesToTwip(0.25),
                        right: convertInchesToTwip(0.25)
                      }
                    })
                  ]
                })
              ]
            });




            const pageBorders = {
              pageBorderTop: {
                style: BorderStyle.DOUBLE,
                size: 6,
                color: "000000",
                space: 3
              },


              pageBorderRight: {
                style: BorderStyle.DOUBLE,
                size: 6,
                color: "000000",
                space: 3
              },
              pageBorderBottom: {
                style: BorderStyle.DOUBLE,
                size: 6,
                color: "000000",
                space: 3
              },
              pageBorderLeft: {
                style: BorderStyle.DOUBLE,
                size: 6,
                color: "000000",
                space: 3
              }
            };

            const doc = new Document({
              sections: [
                {
                  properties: {
                    page: {
                      margin: {
                        top: convertInchesToTwip(0.5),
                        right: convertInchesToTwip(0.5),
                        bottom: convertInchesToTwip(1.0),
                        left: convertInchesToTwip(0.5)
                      },
                      borders: pageBorders,
                      pageNumbers: {
                        start: 1,
                        formatType: NumberFormat.DECIMAL
                      }
                    }
                  },
                  footers: {
                    default: new Footer({
                      children: [
                        footerTable,
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: "Page ",
                              font: "Courier New",
                              size: 16
                            }),
                            new TextRun({
                              children: [PageNumber.CURRENT],
                              font: "Courier New",
                              size: 16
                            }),
                            new TextRun({
                              text: " of ",
                              font: "Courier New",
                              size: 16
                            }),

                            new TextRun({
                              children: [PageNumber.TOTAL_PAGES],
                              font: "Courier New",
                              size: 16
                            })
                          ],
                          alignment: AlignmentType.CENTER,
                          spacing: {
                            before: 200
                          }
                        })
                      ]
                    })
                  },
                  headers: lightWatermarkBase64 ? {
                    default: new Header({
                      children: [
                        new Paragraph({
                          children: [
                            new ImageRun({
                              data: lightWatermarkBase64,
                              transformation: {
                                width: 500,
                                height: 500
                              },
                              floating: {
                                horizontalPosition: {
                                  relative: HorizontalPositionRelativeFrom.PAGE,
                                  align: HorizontalPositionAlign.CENTER
                                },
                                verticalPosition: {
                                  relative: VerticalPositionRelativeFrom.PAGE,
                                  align: VerticalPositionAlign.CENTER
                                },
                                wrap: {
                                  type: TextWrappingType.BEHIND_TEXT
                                },
                                allowOverlap: true,
                                behindDocument: true
                              }
                            })
                          ]
                        })
                      ]
                    })
                  } : undefined,
                  children: documentSections
                }
              ]
            });





            Packer.toBlob(doc).then(blob => {
              resolve(blob);
            }).catch(error => {
       
              reject(error);
            });
          });

        })
      } catch (error) {
   
        reject(error);
      }
    });
  }







  const handleDownloadPDF = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const allFormData = loadAllFormDataFromStorage();

      const safeFormData = form && typeof form === 'object' ? {
        form: form,
        testData: testData
      } : {
        form: allFormData.form || {},
        testData: allFormData.testData || {}
      };



      const wordBlob = await generateWordBlob(safeFormData.form, safeFormData.testData);

      saveAs(wordBlob, `Cable_Test_Report_${safeFormData.form.reportNo || Date.now()}.docx`);


      alert("Data and Word document downloaded successfully");


    } catch (error) {
     
      setErrorMessage(`Error generating Word document: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ca-1001">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="ca-1002">CABLE UPDATE REPORT</h2>

        <span
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            cursor: 'pointer',
            marginLeft: '10px',
            marginBottom: '50px'
          }}
          onClick={redirectToHome}
        >
          X
        </span>
      </div>

      {errorMessage && (
        <div className="error-message" style={{ color: 'red', margin: '10px 0', padding: '8px', backgroundColor: '#ffeeee', borderRadius: '4px' }}>
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="success-message" style={{ color: 'green', margin: '10px 0', padding: '8px', backgroundColor: '#eeffee', borderRadius: '4px' }}>
          {successMessage}
        </div>
      )}

      <div className="ca-1004">
        <div>
          <label>Description</label>
          <input
            id="description"
            type="text"
            value="CABLES INSPECTION / TEST REPORT"
            maxLength={40}
            placeholder="Enter Description"
            onChange={(e) => handleFormChange("description", e.target.value)}
            className={validationErrors.description ? "input-error" : ""}
            readOnly={"description"}
            style={"description" ? {

              cursor: 'not-allowed'
            } : {}}
          />
          {validationErrors.description && (
            <div className="error-text" style={{ color: 'red', fontSize: '12px' }}>
              {validationErrors.description}
            </div>
          )}
        </div>

        <div>
          <label>Date</label>
          <input
            id="date"
            type="date"
            value={form.date || ''}
            onChange={(e) => handleFormChange("date", e.target.value)}
            required
          />
        </div>

        <div>
          <label>Customer</label>
          <input
            id="customer"
            type="text"
            value={form.customer || ''}
            maxLength={40}
            placeholder="Enter Customer"
            onChange={(e) => handleFormChange("customer", e.target.value)}
            className={validationErrors.customer ? "input-error" : ""}
          />
          {validationErrors.customer && (
            <div className="error-text" style={{ color: 'red', fontSize: '12px' }}>
              {validationErrors.customer}
            </div>
          )}
        </div>

        <div>
          <label>Project</label>
          <input
            id="project"
            type="text"
            value={form.project || ''}
            maxLength={40}
            placeholder="Enter Project"
            onChange={(e) => handleFormChange("project", e.target.value)}
            readOnly={"project"}
            style={"project" ? {

              cursor: 'not-allowed'
            } : {}}
          />
        </div>

        <div>
          <label>ReportNo</label>
          <input
            id="reportNo"
            type="text"
            value={form.reportNo || ''}
            maxLength={40}
            placeholder="Enter ReportNo"
            onChange={(e) => handleFormChange("reportNo", e.target.value)}

          />
        </div>

        <div>
          <label>Location</label>
          <input
            id="location"
            type="text"
            value={form.location || ''}
            maxLength={40}
            placeholder="Enter Location"
            onChange={(e) => handleFormChange("location", e.target.value)}
            readOnly={"location"}
            style={"location" ? {

              cursor: 'not-allowed'
            } : {}}
          />
        </div>

        <div>
          <label>Client</label>
          <input
            id="client"
            type="text"
            value={form.client || ''}
            maxLength={40}
            placeholder="Enter Client"
            onChange={(e) => handleFormChange("client", e.target.value)}
            className={validationErrors.client ? "input-error" : ""}
            readOnly={"cleint"}
            style={"client" ? {

              cursor: 'not-allowed'
            } : {}}
          />
          {validationErrors.client && (
            <div className="error-text" style={{ color: 'red', fontSize: '12px' }}>
              {validationErrors.client}
            </div>
          )}
        </div>
                 <div className="SIZ-00">
  <label>Cable Size</label>
  <input 
    type="text" 
    value={form.cablesize || ''}
    maxLength={40}
    placeholder="Enter Cable Size"
    onChange={(e) => handleFormChange("cablesize", e.target.value)}
  />
</div>

<div className="SIZ-01">
  <label>Cable Length</label>
  <input 
    type="text" 
    value={form.cablelength || ''}
    maxLength={40}
    placeholder="Enter Cable Length"
    onChange={(e) => handleFormChange("cablelength", e.target.value)}
  />
</div>

        <div>
          <label>Cable Run Tests</label>
          <select
            id="cable_run_tests"
            value={form.cable_run_tests || ''}
            onChange={(e) => handleFormChange("cable_run_tests", e.target.value)}
          >
            {runOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div>
          <label>Hipot Tests</label>
          <select
            id="hipot_run_tests"
            value={form.hipot_run_tests || ''}
            onChange={(e) => handleFormChange("hipot_run_tests", e.target.value)}
            required
          >
            {testOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="ca-1003">
        <h3 className="ca-1015">INSULATION RESISTANCE TEST</h3>

        {[1, 2, 3, 4].map((run) => {
          const selectedRunNumber = parseInt(selectedCableRuns.replace("Run-", ""));
          if (run > selectedRunNumber) return null;

          return (
            <div className="ca-1009 ca-1014" key={run}>
              <TestInputSection
                title={`BEFORE HIPOT INSULATION RESISTANCE TEST : RUN-${run}`}
                values={testData[`beforeRun${run}`] || getNewPhaseValues()}
                onChange={(i, f, v) => handleTestChange(`beforeRun${run}`, i, f, v)}
                validateMeasuredValueInput={validateMeasuredValueInput}
              />
              <TestInputSection
                title={`AFTER HIPOT INSULATION RESISTANCE TEST : RUN-${run}`}
                values={testData[`afterRun${run}`] || getNewPhaseValues()}
                onChange={(i, f, v) => handleTestChange(`afterRun${run}`, i, f, v)}
                validateMeasuredValueInput={validateMeasuredValueInput}
              />
            </div>
          );
        })}
      </div>

      <div className="ca-1003">
        <h3 className="ca-1015">HIPOT TEST</h3>
        <h4 className="ca-1016">TEST RESULT :</h4><br />

        {[1, 2, 3, 4].map((run) => {
          const selectedTestNumber = parseInt(selectedHipotTests.replace("Test-", ""));
          if (run > selectedTestNumber) return null;

          const hipotData = testData[`hipotRun${run}`] || getNewHipotValues();

          return (
            <div key={run} className="ca-1010 ca-1014">
              <p><strong>RUN - {run}</strong></p>
              <div className="ca-1006">
                <span>Phase <span style={{ color: "red" }}></span></span>
                <span>Applied DC Voltage (KV) <span style={{ color: "red" }}></span></span>
                <span>Measured Leakage Current (mA) <span style={{ color: "red" }}></span></span>
              </div>

              {hipotPhases.map((phase, i) => {
                const hipotItem = hipotData[i] || { voltage: "", leakage: "" };

                return (
                  <div className="ca-1007" key={phase}>
                    <span className="ca-1013">{phase}</span>
                    <input
                      type="text"
                      placeholder="Enter DC Applied Voltage"
                      value={hipotItem.voltage || ""}
                      maxLength={20}
                      onChange={(e) => handleTestChange(`hipotRun${run}`, i, "voltage", e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Enter Measured Leakage Current"
                      value={hipotItem.leakage || ""}
                      maxLength={10}
                      onChange={(e) => handleTestChange(`hipotRun${run}`, i, "leakage", e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="ca-1008">
        <button
          className="cancel-button"
          onClick={goBack}
          disabled={isLoading}
        >
          Back
        </button>
        <button
          className="btn-next"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Processing..." : "Update"}
        </button>
        {formSubmitted && (
          <button
            className="btn-download"
            onClick={handleDownloadPDF}
            disabled={isLoading}
            style={{
              marginLeft: '10px',
              backgroundColor: '#4CAF50',
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            Download Word
          </button>
        )}
      </div>
    </div>
  );
};

const TestInputSection = ({ title, values, onChange, validateMeasuredValueInput }) => {
  const safeValues = Array.isArray(values) && values.length === phases.length
    ? values
    : phases.map(() => ({ voltage: "", measuredValue: "", measuredUnit: "" }));

  return (
    <div className="ca-1010">
      <h4>{title}</h4>
      <div className="ca-1006">
        <span>Phase <span style={{ color: "red" }}></span></span>
        <span>Applied Voltage <span style={{ color: "red" }}></span></span>
        <span>Measured Value <span style={{ color: "red" }}></span></span>
      </div>

      {phases.map((phase, i) => {
        const value = safeValues[i] || { voltage: "", measuredValue: "", measuredUnit: "" };

        return (
          <div className="ca-1007" key={phase}>
            <span className="ca-1013">{phase}</span>
            <select
              value={value.voltage || ""}
              onChange={(e) => onChange(i, "voltage", e.target.value)}
            >
              <option value="">Select Voltage</option>
              {voltageOptions.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>

            <div className="ca-1011">
              <input
                type="text"
                placeholder="Value"
                value={value.measuredValue || ""}
                onChange={(e) => onChange(i, "measuredValue", e.target.value)}
              />
              <select
                value={value.measuredUnit || ""}
                onChange={(e) => onChange(i, "measuredUnit", e.target.value)}
              >
                <option value="">Units</option>
                {unitOptions.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CableTestReport;


